import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { api } from '../api';
import { loadYouTube } from '../utils/youtube';

function fmt(s) {
  s = Math.max(0, Math.round(Number(s) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (h ? `${h}:${String(m).padStart(2, '0')}` : `${m}`) + `:${String(sec).padStart(2, '0')}`;
}

const REPORT_EVERY_SECONDS = 5;   // how often watch time is sent to the server while the video plays

// One assignment: watch a part, the player pauses at its end, answer the quiz one question at a time, and retake until
// every answer is right. Grading happens on the server; this screen never receives the answer key.
//
// The student can't skip ahead: YouTube's own controls and keyboard shortcuts are switched off (there is no timeline to
// drag), our Play/Pause button has a progress bar that is not clickable, any forward jump is undone, and the server
// requires most of a part to have been watched before its first quiz.
export default function VideoSession({ item, onExit, onChanged }) {
  const isRumble = item.video.source === 'rumble';
  const [parts, setParts] = useState(item.parts);
  const firstOpen = item.parts.find(p => !p.complete);
  const [partIdx, setPartIdx] = useState((firstOpen || item.parts[0]).idx);
  const [phase, setPhase] = useState(firstOpen ? 'watch' : 'finished');   // watch | loading | quiz | result | finished
  const [quiz, setQuiz] = useState(null);
  const [qi, setQi] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [notice, setNotice] = useState('');

  const part = parts.find(p => p.idx === partIdx);
  const playerBox = useRef(null);
  const playerRef = useRef(null);
  const clipEnd = useRef(null);
  const quizPanel = useRef(null);
  // the 400 ms timer must always see the latest state, not what it closed over
  const live = useRef({});
  live.current = { phase, part };
  const beginQuizRef = useRef(null);
  const pending = useRef(0);          // seconds watched but not yet reported
  const lastT = useRef(null);         // where the video was at the previous tick (for the skip-ahead guard)
  const lastTickAt = useRef(null);

  async function flushWatch() {
    const seconds = pending.current;
    const pt = live.current.part;
    if (seconds < 0.5 || !pt) return;
    pending.current = 0;
    try { await api.reportVideoWatch(item.id, pt.idx, Math.round(seconds * 10) / 10); }
    catch { pending.current += seconds; }   // try again with the next report
  }
  const flushRef = useRef(flushWatch);
  flushRef.current = flushWatch;

  // ---- the player (YouTube only; Rumble can't be controlled from outside, so it runs in manual mode) ----
  useEffect(() => {
    if (isRumble) return undefined;
    let cancelled = false;
    let timer;
    loadYouTube().then((YT) => {
      if (cancelled) return;
      const first = (item.parts.find(p => !p.complete) || item.parts[0]);
      playerRef.current = new YT.Player(playerBox.current, {
        videoId: item.video.source_key,
        playerVars: {
          start: Math.floor(first.start_s), rel: 0, cc_load_policy: 1, playsinline: 1, modestbranding: 1,
          controls: 0,        // no YouTube timeline/slider to drag
          disablekb: 1,       // no keyboard seeking
          fs: 0, iv_load_policy: 3,
        },
        events: { onReady: () => setPlayerReady(true) },
      });
      timer = setInterval(() => {
        const p = playerRef.current;
        if (!p || !p.getCurrentTime) return;
        const nowMs = performance.now();
        const dt = lastTickAt.current == null ? 0.4 : (nowMs - lastTickAt.current) / 1000;
        lastTickAt.current = nowMs;
        const t = p.getCurrentTime();
        const isPlaying = p.getPlayerState() === 1;
        setPlaying(isPlaying);
        if (clipEnd.current != null) {                     // a review clip is playing
          lastT.current = null;
          if (t >= clipEnd.current) { clipEnd.current = null; p.pauseVideo(); }
          return;
        }
        const { phase: ph, part: pt } = live.current;
        if (ph !== 'watch' || !pt) { lastT.current = null; return; }
        setElapsed(Math.max(0, Math.min(t - pt.start_s, pt.end_s - pt.start_s)));
        if (t < pt.start_s - 1.5) { p.seekTo(pt.start_s, true); lastT.current = null; return; }   // stay within this part
        // A jump ahead of where playback should be is a skip: undo it. Checked in every player state, because a skip
        // can land while the player is paused or buffering, and that new position must never become "where we were".
        if (lastT.current != null && t - lastT.current > dt * 1.5 + 2.5) { p.seekTo(lastT.current, true); return; }
        if (isPlaying) {
          pending.current += Math.min(dt, 2);   // a throttled background tab must not credit time it didn't play
          lastT.current = t;
          if (pending.current >= REPORT_EVERY_SECONDS) flushRef.current();
          if (t >= pt.end_s - 0.2) { p.pauseVideo(); beginQuizRef.current(); }
        } else {
          lastT.current = t;
          if (pending.current >= 1) flushRef.current();
        }
      }, 400);
    }).catch(err => toast.error(err.message));
    return () => {
      cancelled = true;
      clearInterval(timer);
      if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy();
    };
  }, []);

  // ---- quiz ----
  async function beginQuiz() {
    const pt = live.current.part;
    setPhase('loading');
    setNotice('');
    await flushWatch();
    try {
      const q = await api.startVideoAttempt(item.id, pt.idx);
      setQuiz(q); setQi(0); setAnswers({}); setResult(null);
      setPhase('quiz');
    } catch (err) {
      if (err.data && err.data.code === 'watch_more') {
        // reached the end without having watched enough of it: start the part again rather than quizzing
        setNotice(`Please watch this part all the way through before the quiz (you've watched about ${fmt(err.data.watched_s)} of the ${fmt(err.data.needed_s)} needed). Let's start it again.`);
        lastT.current = null;
        if (playerRef.current && playerRef.current.seekTo) {
          playerRef.current.seekTo(pt.start_s, true);
          if (!isRumble) playerRef.current.playVideo();
        }
      } else {
        toast.error(err.message);
      }
      setPhase('watch');
    }
  }
  beginQuizRef.current = beginQuiz;

  useEffect(() => {   // move keyboard/screen-reader focus to the new panel
    if ((phase === 'quiz' || phase === 'result' || phase === 'finished') && quizPanel.current) quizPanel.current.focus();
  }, [phase, qi]);

  async function submit() {
    setPhase('loading');
    try {
      const r = await api.submitVideoAttempt(quiz.attempt_id, answers);
      setResult(r);
      setParts(ps => ps.map(p => (p.idx === partIdx ? { ...p, attempts: p.attempts + 1, complete: p.complete || r.perfect } : p)));
      setPhase('result');
      onChanged();
    } catch (err) {
      toast.error(err.message);
      setPhase('quiz');
    }
  }

  function playClip(m) {
    if (isRumble || !playerRef.current) return;
    clipEnd.current = m.clip_end_s;
    playerRef.current.seekTo(m.clip_start_s, true);
    playerRef.current.playVideo();
    if (playerBox.current && playerBox.current.scrollIntoView) playerBox.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  function togglePlay() {
    const p = playerRef.current;
    if (!p || phase !== 'watch') return;
    if (p.getPlayerState() === 1) p.pauseVideo(); else p.playVideo();
  }

  function goNext() {
    const next = parts.find(p => p.idx > partIdx && !p.complete) || parts.find(p => !p.complete);
    if (!next) { setPhase('finished'); return; }
    setPartIdx(next.idx);
    setPhase('watch');
    setQuiz(null); setResult(null); setNotice(''); setElapsed(0);
    clipEnd.current = null;
    lastT.current = null;
    pending.current = 0;
    if (playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(next.start_s, true);
      playerRef.current.playVideo();
    }
  }

  const allDone = parts.every(p => p.complete);
  const q = quiz && quiz.questions[qi];
  const answeredAll = quiz && quiz.questions.every(x => answers[x.id] !== undefined);
  const partLength = part ? part.end_s - part.start_s : 0;

  return (
    <div className="vq-session">
      <div className="vq-bar">
        <button type="button" className="btn-secondary btn-small" onClick={onExit}>← Back to my list</button>
        <h2>{item.video.title || 'Video'}</h2>
        <ol className="vq-parts" aria-label="Parts in this assignment">
          {parts.map(p => (
            <li key={p.idx} className={`${p.complete ? 'done' : ''} ${p.idx === partIdx && phase !== 'finished' ? 'current' : ''}`}>
              Part {p.idx}{p.complete ? ' ✓' : ''}
            </li>
          ))}
        </ol>
      </div>

      {isRumble ? (
        <div className="vq-player"><iframe title={item.video.title || 'Video'} allowFullScreen
          src={`https://rumble.com/embed/${item.video.embed_id || ''}/`} /></div>
      ) : (
        <div className="vq-player"><div ref={playerBox} /></div>
      )}

      {!isRumble && phase !== 'finished' && part && (
        <div className="vq-controls">
          <button type="button" className="btn-primary" onClick={togglePlay} disabled={!playerReady || phase !== 'watch'}
                  aria-label={playing ? 'Pause the video' : 'Play the video'}>
            {playing ? '⏸ Pause' : '▶ Play'}
          </button>
          <progress value={elapsed} max={partLength || 1} aria-label="How far through this part you are" />
          <span className="vq-time" aria-hidden="true">{fmt(elapsed)} / {fmt(partLength)}</span>
        </div>
      )}

      {notice && <p className="video-error" role="alert">{notice}</p>}

      {phase !== 'finished' && part && (
        <p className="vq-now" role="status">
          Now: part {part.idx}{part.title ? `, ${part.title}` : ''} ({fmt(part.start_s)}–{fmt(part.end_s)}).
          {phase === 'watch' && (isRumble
            ? ' Watch that stretch, then press the button below.'
            : playerReady ? ' Press Play. The video stops by itself at the end of the part, and then comes your quiz.' : ' Loading the video…')}
        </p>
      )}

      {phase === 'watch' && isRumble && (
        <button type="button" className="btn-primary" onClick={beginQuiz}>I've watched this part: take the quiz</button>
      )}

      {phase === 'loading' && <div className="loading" role="status">One moment…</div>}

      {phase === 'quiz' && q && (
        <section className="vq-panel" ref={quizPanel} tabIndex={-1} aria-labelledby="vq-qhead">
          <h3 id="vq-qhead">Question {qi + 1} of {quiz.questions.length}</h3>
          <fieldset className="vq-fieldset">
            <legend>{q.prompt}</legend>
            {q.choices.map((c, i) => (
              <label key={i} className={`vq-choice ${answers[q.id] === i ? 'picked' : ''}`}>
                <input type="radio" name={`q-${q.id}`} checked={answers[q.id] === i}
                       onChange={() => setAnswers({ ...answers, [q.id]: i })} />
                <span>{c}</span>
              </label>
            ))}
          </fieldset>
          <div className="vq-nav">
            {qi > 0 && <button type="button" className="btn-secondary" onClick={() => setQi(qi - 1)}>Back</button>}
            {qi < quiz.questions.length - 1
              ? <button type="button" className="btn-primary" disabled={answers[q.id] === undefined} onClick={() => setQi(qi + 1)}>Next</button>
              : <button type="button" className="btn-primary" disabled={!answeredAll} onClick={submit}>Submit answers</button>}
          </div>
        </section>
      )}

      {phase === 'result' && result && (
        <section className="vq-panel" ref={quizPanel} tabIndex={-1} aria-labelledby="vq-rhead">
          <h3 id="vq-rhead">
            {result.perfect ? 'Perfect score! 🎉' : `You got ${result.score} of ${result.total}`}
          </h3>
          {result.perfect ? (
            <>
              <p>{allDone || result.assignment_completed ? 'You finished this assignment. Nice work!' : 'Part complete.'}</p>
              {allDone || result.assignment_completed
                ? <button type="button" className="btn-primary" onClick={() => setPhase('finished')}>Done</button>
                : <button type="button" className="btn-primary" onClick={goNext}>Continue to the next part</button>}
            </>
          ) : (
            <>
              <p>You need every answer right to finish this part. Watch the clip for each one you missed, then try again.</p>
              <ul className="vq-missed">
                {result.missed.map(m => (
                  <li key={m.question_id}>
                    <span>{m.prompt}</span>
                    {isRumble
                      ? <span className="vq-hint">Watch around {fmt(m.clip_start_s)}–{fmt(m.clip_end_s)} in the video.</span>
                      : <button type="button" className="btn-small" onClick={() => playClip(m)}>Watch the clip</button>}
                  </li>
                ))}
              </ul>
              <button type="button" className="btn-primary" onClick={beginQuiz}>Try the quiz again</button>
            </>
          )}
        </section>
      )}

      {phase === 'finished' && (
        <section className="vq-panel" ref={quizPanel} tabIndex={-1} aria-labelledby="vq-fhead">
          <h3 id="vq-fhead">All done ✓</h3>
          <p>You've finished every part of this assignment.</p>
          <button type="button" className="btn-primary" onClick={onExit}>Back to my list</button>
        </section>
      )}
    </div>
  );
}
